import {yupResolver} from "@hookform/resolvers/yup"
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  styled,
  Typography,
} from "@mui/material"
import {t} from "i18next"
import {Controller, useForm} from "react-hook-form"
import * as yup from "yup"
import {areasOfInterest, geographyOfInterest} from "./constants"
import {NewButton, Tooltip} from "../../../components"
import {useProfileContext} from "../Profile"
import {useEffect, useState} from "react"
import {useConfigProvider} from "../../../config"
import {decodeToken} from "react-jwt"
import {Token, UpdateCRMContact} from "../../../types"
import {useMutation} from "@tanstack/react-query"
import {accountEndpoints, fabricEndpoints} from "../../../services"
import {toast} from "react-toastify"
import {SuccessIcon} from "../../../assets"
import {StorageData} from "../../../hooks/useStorage"
import {useSessionStorage} from "usehooks-ts"
import ConfirmationDialog from "../../../components/molecules/ConfirmationDialog/ConfirmationDialog"
import {appInsights} from "../../../config/appInsights"
import {useExpiringLocalStorage} from "../../../hooks/useExpiringLocalStorage"
import {CrmAccount} from "../../../types/CrmAccount"

const preferencesSchema = yup.object().shape({
  selectedGeographies: yup.array().of(yup.string()).notRequired(),
  selectedAreas: yup.array().of(yup.string()).notRequired(),
})

type PreferencesFormValues = yup.InferType<typeof preferencesSchema>

const Preferences = () => {
  const {
    control: preferencesControl,
    handleSubmit: preferencesHandleSubmit,
    reset,
    watch,
  } = useForm<PreferencesFormValues>({
    defaultValues: {
      selectedGeographies: [],
      selectedAreas: [],
    },
    resolver: yupResolver(preferencesSchema),
  })
  const {token} = useConfigProvider()
  const decoded = decodeToken<Token>(token)
  const {profileQuery} = useProfileContext()

  const [_, setCompleted] = useSessionStorage<boolean | undefined>(
    StorageData.completedPreferences,
    undefined,
  )
  const [getTempPreferences, setTempPreferences] =
    useExpiringLocalStorage<string>(
      "tempPreferences",
      "",
      10 * 60 * 1000, //expire in 10 minutes
    )
  const [showCancelPreferencesDialog, setShowCancelPreferencesDialog] =
    useState(false)
  const {updateContact, completePreferences} = accountEndpoints()
  const {sendFabricEvents} = fabricEndpoints()

  const tempPreferencesArray: string[] | undefined = !!getTempPreferences()
    ? JSON.parse(getTempPreferences())
    : undefined

  const updateCRMContactMutation = useMutation({
    mutationFn: updateContact,
    mutationKey: ["updateCRMContact"],
  })

  const completePreferencesMutation = useMutation<boolean, Error, string>({
    mutationFn: (data) => completePreferences(data),
    onSuccess: (data) => {
      setCompleted(true)
    },
  })

  const sendFabricEventsMutation = useMutation({
    mutationFn: sendFabricEvents,
    mutationKey: ["sendFabricEvents"],
    throwOnError: false,
  })

  useEffect(() => {
    if (!profileQuery.isLoading && profileQuery.data?.contactId) {
      reset({
        selectedAreas:
          (tempPreferencesArray &&
            tempPreferencesArray.filter((item) =>
              areasOfInterest.some((area) => area.value === item),
            )) ||
          profileQuery.data?.marketingAreas?.map((item) =>
            item.toLowerCase(),
          ) ||
          undefined,
        selectedGeographies:
          (tempPreferencesArray &&
            tempPreferencesArray.filter((item) =>
              geographyOfInterest.some((area) => area.value === item),
            )) ||
          profileQuery.data?.marketingGeographies?.map((item) =>
            item.toLowerCase(),
          ) ||
          undefined,
      })
    }
  }, [profileQuery.isLoading, profileQuery.data?.contactId])

  const handleResetPreferences = () => {
    reset({
      selectedAreas:
        profileQuery.data?.marketingAreas?.map((item) => item.toLowerCase()) ||
        undefined,
      selectedGeographies:
        profileQuery.data?.marketingGeographies?.map((item) =>
          item.toLowerCase(),
        ) || undefined,
    })
    setShowCancelPreferencesDialog(false)
  }

  const goiColumns = splitIntoVerticalColumns(geographyOfInterest, 2)
  const aoiColumns = splitIntoVerticalColumns(areasOfInterest, 2)

  const isSaveDisabled =
    arraysAreEqual(
      (
        tempPreferencesArray?.filter((item) =>
          areasOfInterest.some((area) => area.value === item),
        ) ||
        profileQuery.data?.marketingAreas ||
        []
      ).filter((area): area is string => area !== undefined),
      (watch()?.selectedAreas || []).filter(
        (area): area is string => area !== undefined,
      ),
    ) &&
    arraysAreEqual(
      (
        tempPreferencesArray?.filter((item) =>
          geographyOfInterest.some((area) => area.value === item),
        ) ||
        profileQuery.data?.marketingGeographies ||
        []
      ).filter((area): area is string => area !== undefined),
      (watch()?.selectedGeographies || []).filter(
        (area): area is string => area !== undefined,
      ),
    )

  const handleSubmitPreferences = async (data: PreferencesFormValues) => {
    if (profileQuery.data?.accountId || decoded?.companyId) {
      const selectedPreferences = [
        ...(data.selectedGeographies ?? []).filter(
          (item): item is string => item !== undefined,
        ),
        ...(data.selectedAreas ?? []).filter(
          (item): item is string => item !== undefined,
        ),
      ]
      const updatePayload = {
        _cr_accountname_value:
          profileQuery?.data?.accountId || decoded?.companyId || "",
        contactid: profileQuery.data?.contactId || "",
        emailaddress1: decoded?.email || "",
        firstname: profileQuery.data?.firstName || "",
        lastname: profileQuery.data?.lastName || "",
        telephone1: profileQuery.data?.businessPhone,
        Jobtitle: profileQuery.data?.jobTitle,
        new_jobfunction: profileQuery.data?.jobFunction,
        cr_language: profileQuery.data?.preferredLanguage?.toString(),
        marketingAreas: areasOfInterest.map((area) => [
          area.value,
          selectedPreferences?.includes(area.value) ? "1" : "0",
        ]),
        marketingGeographies: geographyOfInterest.map((geography) => [
          geography.value,
          selectedPreferences?.includes(geography.value) ? "1" : "0",
        ]),
      } satisfies UpdateCRMContact

      await updateCRMContactMutation.mutateAsync(updatePayload)
      if (!updateCRMContactMutation.isError) {
        const tempPreferencesValue = JSON.stringify(selectedPreferences)
        setTempPreferences(tempPreferencesValue)
        await completePreferencesMutation.mutateAsync(decoded?.email!)
        appInsights.trackEvent({
          name: "MarketingPreferences",
          properties: {
            type: "MarketingPreferences",
            targetId: profileQuery.data?.contactId || "",
            targetName: `${profileQuery.data?.firstName} ${profileQuery.data?.lastName}`,
            appUrl: window.location.href,
            userId: decoded?.contactId,
            signinidentifier: decoded?.email,
            userdisplayname: decoded?.name,
            company: profileQuery.data?.accountNumber,
            groups: null,
          },
        })
        await sendFabricEventsMutation.mutateAsync([
          {
            type: "MarketingPreferences",
            targetId: profileQuery.data?.contactId || "",
            targetName: `${profileQuery.data?.firstName} ${profileQuery.data?.lastName}`,
            appUrl: window.location.href,
          },
        ])
        toast.success("Your marketing preferences have been saved", {
          autoClose: 3000,
          style: {
            borderLeftColor: "#58A733",
          },
          icon: SuccessIcon,
        })
      }
    }
  }

  return (
    <>
      <Stack
        component="main"
        id="profile-details"
        aria-label="Profile Details"
        direction="row"
        height="100%"
        paddingX="32px"
        paddingY="24px"
      >
        <Stack
          component="section"
          direction="column"
          width="100%"
          height="100%"
          paddingY="29px"
          bgcolor="#ffffff"
          borderRadius="8px"
          boxShadow="0 4px 8px 0 #00000019"
          gap="32px"
        >
          <Stack
            width="766px"
            component="form"
            onSubmit={preferencesHandleSubmit(handleSubmitPreferences)}
            paddingBottom="24px"
            paddingX="32px"
            gap="16px"
          >
            <Stack gap="16px" sx={{overflowY: "auto"}}>
              <Typography
                component="p"
                fontSize="14px"
                fontWeight={400}
                color="#242d35"
                lineHeight="1.43"
                textAlign="left"
              >
                {t("Please confirm your marketing preferences.")}
              </Typography>
              <Typography
                component="p"
                fontSize="16px"
                fontWeight={700}
                color="#053747"
                lineHeight="1.43"
                textAlign="left"
              >
                {t("Areas of interest")}
              </Typography>
              <Grid container>
                {aoiColumns.map((column, colIndex) => (
                  <Grid size={6} key={colIndex}>
                    <FormGroup>
                      {column.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          sx={{
                            marginLeft: "0px !important",
                          }}
                          control={
                            <Controller
                              name="selectedAreas"
                              control={preferencesControl}
                              render={({field}) => (
                                <Checkbox
                                  checked={
                                    Array.isArray(field.value) &&
                                    field.value.includes(option.value)
                                  }
                                  onChange={(e) => {
                                    const currentValues: string[] = (
                                      field.value || []
                                    ).filter(
                                      (val): val is string => val !== undefined,
                                    )
                                    const newValue = e.target.checked
                                      ? [...currentValues, option.value]
                                      : currentValues.filter(
                                          (val) => val !== option.value,
                                        )
                                    field.onChange(newValue)
                                  }}
                                  style={{
                                    paddingTop: "8px",
                                    paddingBottom: "8px",
                                    paddingLeft: "8px",
                                    paddingRight: "8px",
                                  }}
                                  disabled={
                                    decoded?.companyId === CrmAccount.Internal
                                  }
                                />
                              )}
                            />
                          }
                          label={t(option.label)}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                ))}
              </Grid>
              <Typography
                component="p"
                fontSize="16px"
                fontWeight={700}
                color="#053747"
                lineHeight="1.43"
                textAlign="left"
              >
                {t("Geography of interest")}
              </Typography>
              <Grid container>
                {goiColumns.map((column, colIndex) => (
                  <Grid size={6} key={colIndex}>
                    <FormGroup>
                      {column.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          sx={{
                            marginLeft: "0px !important",
                          }}
                          control={
                            <Controller
                              name="selectedGeographies"
                              control={preferencesControl}
                              render={({field}) => (
                                <Checkbox
                                  checked={
                                    Array.isArray(field.value) &&
                                    field.value.includes(option.value)
                                  }
                                  onChange={(e) => {
                                    const currentValues: string[] = (
                                      field.value || []
                                    ).filter(
                                      (val): val is string => val !== undefined,
                                    )
                                    const newValue = e.target.checked
                                      ? [...currentValues, option.value]
                                      : currentValues.filter(
                                          (val) => val !== option.value,
                                        )
                                    field.onChange(newValue)
                                  }}
                                  style={{
                                    paddingTop: "8px",
                                    paddingBottom: "8px",
                                    paddingLeft: "8px",
                                    paddingRight: "8px",
                                  }}
                                  disabled={
                                    decoded?.companyId === CrmAccount.Internal
                                  }
                                />
                              )}
                            />
                          }
                          label={t(option.label)}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                ))}
              </Grid>
            </Stack>
            <Stack
              component="footer"
              flexDirection="row"
              alignItems="center"
              justifyContent="end"
              gap="24px"
              paddingTop="8px"
              px="32px"
            >
              <ConfirmationDialog
                showDialog={showCancelPreferencesDialog}
                title={t("Preferences not saved")}
                message={t(
                  "Your marketing preferences have not been saved. Proceed? No/Yes",
                )}
                handleClose={() => setShowCancelPreferencesDialog(false)}
                handleConfirm={() => handleResetPreferences()}
                closeText={t("No")}
                confirmText={t("Yes")}
                closeIcon
              />

              <NewButton
                type="button"
                text={t("Cancel")}
                onClick={() => {
                  setShowCancelPreferencesDialog(true)
                }}
                variant="underline"
                color="primary"
                size="medium"
                sx={{
                  paddingX: "0",
                  paddingBottom: "0",
                  borderRadius: "0",
                }}
              />
              <Tooltip
                title={t("No changes made")}
                arrow
                placement="bottom"
                PopperProps={{
                  style: {
                    display: isSaveDisabled ? "block" : "none",
                  },
                }}
              >
                <Stack>
                  <NewButton
                    loading={updateCRMContactMutation.isPending}
                    type="submit"
                    text={t("Save")}
                    variant="filled"
                    color="primary"
                    size="medium"
                    disabled={
                      isSaveDisabled ||
                      decoded?.companyId === CrmAccount.Internal
                    }
                    sx={{
                      minWidth: "136px",
                    }}
                  />
                </Stack>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </>
  )
}

export default Preferences

const FormGroup = styled("article")(({theme}) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flexWrap: "nowrap",
  flex: 1,
}))

const splitIntoVerticalColumns = (
  items: {label: string; value: string}[],
  columns: number,
) => {
  const sortedItems = [...items].sort((a, b) => a.label.localeCompare(b.label))
  const columnLength = Math.ceil(sortedItems.length / columns)

  return Array.from({length: columns}, (_, i) =>
    sortedItems.slice(i * columnLength, (i + 1) * columnLength),
  )
}

function arraysAreEqual(arr1: string[], arr2: string[]): boolean {
  if (arr1.length !== arr2.length) return false
  return arr1.every(
    (value, index) => value.toLowerCase() === arr2[index].toLowerCase(),
  )
}
